import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Sparkles, TrendingUp, Timer, Trophy, Target, Grid3x3 } from "lucide-react";
import { powerHome, powerBookmarkToggle, POWER_CATEGORIES, compactNumber } from "@/lib/powerApi";
import PetitionCard from "@/components/power/PetitionCard";
import PowerShareDialog from "@/components/power/PowerShareDialog";

export default function PowerHome() {
  const [data, setData] = useState(null);
  const [sharing, setSharing] = useState(null);
  const [bookmarks, setBookmarks] = useState({});

  useEffect(() => {
    powerHome().then(setData).catch(() => toast.error("Couldn't load petitions"));
  }, []);

  const toggleBookmark = async (p) => {
    try {
      const r = await powerBookmarkToggle(p.slug);
      setBookmarks((b) => ({ ...b, [p.slug]: r.bookmarked }));
      toast.success(r.bookmarked ? "Bookmarked" : "Removed from bookmarks");
    } catch (e) {
      if (e.response?.status === 401) toast.error("Sign in to bookmark");
      else toast.error("Couldn't bookmark");
    }
  };

  if (!data) return <SkeletonHome />;

  return (
    <div data-testid="power-home" className="space-y-8 pb-6">
      <Hero />

      <Rail title="Trending now" icon={<TrendingUp className="w-4 h-4" />} to="/power/explore?sort=trending"
        items={data.trending} onShare={setSharing} onBookmark={toggleBookmark} bookmarks={bookmarks} testId="rail-trending" />

      <Rail title="Featured petitions" icon={<Sparkles className="w-4 h-4" />} to="/power/explore?sort=featured"
        items={data.featured} onShare={setSharing} onBookmark={toggleBookmark} bookmarks={bookmarks} testId="rail-featured" />

      <PopularCategories cats={data.popular_categories} />

      <Rail title="Almost at the goal" icon={<Target className="w-4 h-4" />} to="/power/explore?sort=close_to_goal"
        items={data.close_to_goal} onShare={setSharing} onBookmark={toggleBookmark} bookmarks={bookmarks} testId="rail-close" />

      <Rail title="Newest petitions" icon={<Timer className="w-4 h-4" />} to="/power/explore?sort=newest"
        items={data.newest} onShare={setSharing} onBookmark={toggleBookmark} bookmarks={bookmarks} testId="rail-newest" />

      <Rail title="Recently completed" icon={<Trophy className="w-4 h-4" />} to="/power/explore?sort=completed"
        items={data.completed} onShare={setSharing} onBookmark={toggleBookmark} bookmarks={bookmarks} testId="rail-completed" />

      {data.success_stories?.length > 0 && (
        <SuccessStories items={data.success_stories} />
      )}

      {sharing && <PowerShareDialog url={`${window.location.origin}/power/p/${sharing.slug}`} title={sharing.title} onClose={() => setSharing(null)} />}
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1E88FF] via-[#1E88FF] to-[#0A1A2F] text-white p-6 sm:p-10 shadow-xl">
      <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
      <div className="absolute -bottom-20 -left-16 w-64 h-64 rounded-full bg-[#22C55E]/20 blur-3xl" />
      <div className="relative">
        <div className="inline-flex items-center gap-1 bg-white/15 backdrop-blur px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest">
          <Sparkles className="w-3 h-3" /> Petitions that move
        </div>
        <h1 className="mt-3 text-3xl sm:text-5xl font-black tracking-tight leading-[1.05]">
          The change you<br />want. <span className="text-[#7BB8FF]">Powered by people.</span>
        </h1>
        <p className="mt-3 text-white/80 max-w-md text-sm sm:text-base">
          Start a petition, gather signatures, share your story — and get rewarded for every step of the movement.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          <Link to="/power/create" data-testid="hero-cta-create"
            className="inline-flex items-center gap-2 bg-white text-[#0A1A2F] font-bold px-5 py-3 rounded-full shadow hover:scale-[1.03] transition-transform">
            Start a petition <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/power/explore" data-testid="hero-cta-explore"
            className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white font-semibold px-5 py-3 rounded-full hover:bg-white/20 transition-colors">
            Explore petitions
          </Link>
        </div>
      </div>
    </section>
  );
}

function Rail({ title, icon, to, items, onShare, onBookmark, bookmarks, testId }) {
  if (!items || items.length === 0) return null;
  return (
    <section data-testid={testId}>
      <header className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-[#1E88FF]/10 text-[#1E88FF] flex items-center justify-center">{icon}</div>
        <h2 className="font-black text-xl text-[#0A1A2F] tracking-tight">{title}</h2>
        <Link to={to} className="ml-auto text-xs font-semibold text-[#1E88FF] hover:underline flex items-center gap-1">
          See all <ArrowRight className="w-3 h-3" />
        </Link>
      </header>
      <div className="flex gap-3 overflow-x-auto hide-scrollbar snap-x snap-mandatory -mx-4 px-4 pb-2">
        {items.map((p) => (
          <div key={p.slug} className="snap-start">
            <PetitionCard petition={p} compact onShare={onShare} onBookmark={onBookmark} bookmarked={!!bookmarks[p.slug]} />
          </div>
        ))}
      </div>
    </section>
  );
}

function PopularCategories({ cats }) {
  const items = (cats && cats.length ? cats : POWER_CATEGORIES.slice(0, 8).map((n) => ({ name: n, count: 0 })));
  return (
    <section data-testid="rail-categories">
      <header className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-[#1E88FF]/10 text-[#1E88FF] flex items-center justify-center"><Grid3x3 className="w-4 h-4" /></div>
        <h2 className="font-black text-xl text-[#0A1A2F] tracking-tight">Popular categories</h2>
      </header>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {items.map((c) => (
          <Link key={c.name} to={`/power/explore?category=${encodeURIComponent(c.name)}`}
            data-testid={`cat-tile-${c.name}`}
            className="group bg-white rounded-2xl border border-[#E4EAF3] px-4 py-3 hover:border-[#1E88FF] hover:shadow-md transition-all">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#0A1A2F]/40 group-hover:text-[#1E88FF]">{c.count ? `${compactNumber(c.count)} live` : "Explore"}</div>
            <div className="font-bold text-[#0A1A2F] text-sm mt-0.5 truncate">{c.name}</div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function SuccessStories({ items }) {
  return (
    <section data-testid="rail-success">
      <header className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-[#22C55E]/10 text-[#22C55E] flex items-center justify-center"><Trophy className="w-4 h-4" /></div>
        <h2 className="font-black text-xl text-[#0A1A2F] tracking-tight">Success stories</h2>
      </header>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((p) => (
          <Link key={p.slug} to={`/power/p/${p.slug}`}
            className="bg-gradient-to-br from-[#22C55E]/10 to-[#1E88FF]/10 rounded-2xl border border-[#22C55E]/20 p-4 hover:shadow-lg transition-shadow">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#22C55E] mb-1">Victory</div>
            <div className="font-black text-[#0A1A2F] text-lg leading-snug line-clamp-2">{p.title}</div>
            <div className="text-xs text-[#0A1A2F]/60 mt-1">
              {compactNumber(p.signature_count)} signatures reached the goal
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function SkeletonHome() {
  return (
    <div data-testid="power-home-loading" className="space-y-6 py-6">
      <div className="h-56 rounded-3xl bg-[#E4EAF3] animate-pulse" />
      <div className="h-48 rounded-2xl bg-[#E4EAF3] animate-pulse" />
      <div className="h-48 rounded-2xl bg-[#E4EAF3] animate-pulse" />
    </div>
  );
}
